/**
 * 裝櫃批次 batch lens (S4.3) — REGISTERED but its tab stays hidden until W6
 * seeds batches (hide-don't-disable: the deep link `/auth/occ/batches`
 * renders this today; W6 flips `tabHidden` in the lens registry).
 *
 * Owned read model: `selectBatchLensRows` sums the same ShipmentBatchLine
 * facts the shipping page sums, so the utilization/CBM numbers agree by
 * construction. Row → BatchDrawer; write-back stays on the 裝櫃批次 module.
 */

import { useState } from 'react';
import { Button, Progress, Table, Tag, theme } from 'antd';
import type { TableProps } from 'antd';
import { EyeOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { useOccBatchLens } from '@/hooks/useOcc';
import type { OccBatchLensRow } from '@/lib/occ/selectors';
import type { ShipmentBatchStatus } from '@/types/api/shipping';
import { LensFooter, LensUnavailableNotice } from '../lensShared';
import BatchDrawer from '../BatchDrawer';

const STATUS_TAG_COLOR: Record<ShipmentBatchStatus, string> = {
  open: 'default',
  cut_off: 'orange',
  departed: 'cyan',
  closed: 'green',
};

const BatchLens = () => {
  const { t } = useTranslation(['occ', 'common']);
  const { token } = theme.useToken();
  const { rows, syncedAt, isLoading, error, refresh, unavailable } = useOccBatchLens();
  // Record + open are decoupled so the closing animation keeps its content.
  const [drawerBatchId, setDrawerBatchId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openDrawer = (batchId: string) => {
    setDrawerBatchId(batchId);
    setDrawerOpen(true);
  };

  const columns: TableProps<OccBatchLensRow>['columns'] = [
    {
      title: t('occ:occ.batches.columns.batch'),
      key: 'batch',
      width: 160,
      render: (_: unknown, row) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>{row.batchNumber}</span>
      ),
    },
    {
      title: t('occ:occ.batches.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: ShipmentBatchStatus) => (
        <Tag color={STATUS_TAG_COLOR[status]} style={{ margin: 0 }}>
          {t(`occ:occ.batches.status.${status}`)}
        </Tag>
      ),
    },
    {
      title: t('occ:occ.batches.columns.container'),
      dataIndex: 'containerNumber',
      key: 'containerNumber',
      width: 130,
      render: (value: string | null) => renderDash(value),
    },
    {
      title: t('occ:occ.batches.columns.etd'),
      dataIndex: 'etd',
      key: 'etd',
      width: 110,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('occ:occ.batches.columns.eta'),
      dataIndex: 'eta',
      key: 'eta',
      width: 110,
      render: (value: string) => renderDash(value),
    },
    {
      title: t('occ:occ.batches.columns.lines'),
      dataIndex: 'lineCount',
      key: 'lineCount',
      width: 80,
      align: 'right',
      render: (value: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>,
    },
    {
      title: t('occ:occ.batches.columns.loaded'),
      key: 'loaded',
      width: 210,
      render: (_: unknown, row) => (
        <div className="tw-flex tw-flex-col tw-gap-0.5" style={{ maxWidth: 190 }}>
          <div className="tw-flex tw-items-center tw-gap-2">
            <Progress
              percent={Math.min(100, row.loadedPct)}
              showInfo={false}
              size="small"
              style={{ flex: 1, margin: 0 }}
            />
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: token.fontSizeSM }}>
              {row.loadedPct}%
            </span>
          </div>
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontSize: token.fontSizeSM,
              color: token.colorTextSecondary,
            }}
          >
            {row.qtyLoaded} / {row.qtyPlanned}
          </span>
        </div>
      ),
    },
    {
      title: t('occ:occ.batches.columns.cbm'),
      dataIndex: 'cbmTotal',
      key: 'cbmTotal',
      width: 90,
      align: 'right',
      render: (value: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>,
    },
    {
      title: t('occ:occ.batches.columns.shortLoaded'),
      key: 'shortLoaded',
      width: 110,
      align: 'right',
      render: (_: unknown, row) =>
        row.shortLoadedLines > 0 ? (
          <span style={{ color: token.colorError, fontVariantNumeric: 'tabular-nums' }}>
            <WarningOutlined
              aria-label={t('occ:occ.batches.columns.shortLoaded')}
              style={{ marginRight: token.marginXXS }}
            />
            {row.shortLoadedLines}
          </span>
        ) : (
          renderDash()
        ),
    },
    {
      title: t('occ:occ.batches.columns.actions'),
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_: unknown, row) => (
        <Button
          type="text"
          aria-label={t('occ:occ.batches.actions.actionsFor', { ref: row.batchNumber })}
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            openDrawer(row.batchId);
          }}
        />
      ),
    },
  ];

  return (
    <>
      <LensUnavailableNotice active={unavailable} domain="batches" />
      <TableContainer
        errorState={{
          active: Boolean(error),
          title: t('occ:occ.states.loadFailed'),
          retryLabel: t('common:errors.boundary.retry'),
          onRetry: refresh,
        }}
      >
        <Table<OccBatchLensRow>
          rowKey="batchId"
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          onRow={(row) => ({
            onClick: () => openDrawer(row.batchId),
            style: { cursor: 'pointer' },
          })}
          locale={{ emptyText: <ListEmptyState title={t('occ:occ.states.noBatches')} /> }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          scroll={{ x: 1190 }}
        />
      </TableContainer>
      <LensFooter syncedAt={syncedAt} />

      <BatchDrawer
        open={drawerOpen}
        batchId={drawerBatchId}
        syncedAt={syncedAt}
        onClose={() => setDrawerOpen(false)}
      />
    </>
  );
};

export default BatchLens;
